import { usd } from "@/lib/calc";
import { Button } from "@/components/ui/button";
import { FileDown, Loader2, MapPin, Mail, Calculator as CalcIcon, Lock } from "lucide-react";

const Row = ({ label, sub, value, testid, muted }) => (
  <div className="flex items-start justify-between gap-4 py-2.5" data-testid={testid}>
    <div>
      <div className="text-sm font-medium text-slate-700">{label}</div>
      {sub && <div className="text-xs text-slate-400">{sub}</div>}
    </div>
    <div className={`font-mono-plex text-sm font-semibold ${muted ? "text-slate-300" : "text-slate-900"}`}>
      {muted ? "—" : usd(value)}
    </div>
  </div>
);

export default function BreakdownPanel({
  location, breakdown, inputs, hasResult, calculating, isAuthed, onCalculate, onDownload, onEmail, saving, emailing,
}) {
  const m = !hasResult; // muted (no result yet)
  return (
    <div className="sticky top-24 overflow-hidden rounded-sm border border-slate-200 bg-white shadow-sm">
      <div className="bg-slate-900 px-6 py-5 text-white">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
          <MapPin className="h-3.5 w-3.5" />
          {location ? location.label : "Select a location"}
        </div>
        <div className="mt-1 font-heading text-lg font-bold tracking-tight">Cost Breakdown</div>
      </div>

      <div className="px-6 py-4">
        <div className="divide-y divide-slate-100">
          <Row label="Storage of goods" sub={`${inputs.storageSqft || 0} sqft · ${inputs.storageMonths || 0} mo`} value={breakdown.storage} muted={m} testid="row-storage" />
          <Row label="Inbound receiving" sub={`${inputs.inboundPallets || 0} pallets in`} value={breakdown.inbound} muted={m} testid="row-inbound" />
          <Row label="Handling & picking" sub={`${inputs.monthlyOrders || 0} orders`} value={breakdown.pickLabor} muted={m} testid="row-pick" />
          <Row label="Repacking" sub={`${inputs.repackUnits || 0} units`} value={breakdown.repackLabor} muted={m} testid="row-repack" />
          <Row label="Outbound / shipping" sub={`${inputs.outboundPallets || 0} pallets out`} value={breakdown.outbound} muted={m} testid="row-outbound" />
        </div>

        <div className="mt-3 border-t border-slate-200 pt-3">
          <Row label="Subtotal" value={breakdown.subtotal} muted={m} testid="row-subtotal" />
          <Row label={`G&A / Project Mgmt (${inputs.gaPercent || 0}%)`} value={breakdown.ga} muted={m} testid="row-ga" />
        </div>

        <div className="mt-3 flex items-end justify-between border-t-2 border-slate-900 pt-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
              Est. Monthly Total
            </div>
            <div className="text-xs text-slate-400">before applicable taxes</div>
          </div>
          <div className={`font-heading text-3xl font-extrabold tracking-tight ${m ? "text-slate-300" : "text-blue-600"}`} data-testid="grand-total">
            {m ? "—" : usd(breakdown.total)}
          </div>
        </div>

        <Button
          onClick={onCalculate}
          disabled={calculating || !location}
          className="mt-5 h-12 w-full rounded-sm bg-blue-600 text-sm font-semibold text-white transition-colors duration-200 hover:bg-blue-700 disabled:opacity-50"
          data-testid="calculate-btn"
        >
          {calculating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : !isAuthed ? <Lock className="mr-2 h-4 w-4" /> : <CalcIcon className="mr-2 h-4 w-4" />}
          {calculating ? "Calculating…" : !isAuthed ? "Sign in to Calculate" : hasResult ? "Recalculate Estimate" : "Calculate Estimate"}
        </Button>

        <Button
          onClick={onDownload}
          disabled={saving || emailing || !hasResult}
          variant="outline"
          className="mt-2.5 h-11 w-full rounded-sm border-slate-300 text-sm font-semibold text-slate-800 transition-colors duration-200 hover:bg-slate-100 disabled:opacity-50"
          data-testid="download-pdf-btn"
        >
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
          {saving ? "Generating…" : "Download PDF Quote"}
        </Button>
        <Button
          onClick={onEmail}
          disabled={saving || emailing || !hasResult}
          variant="outline"
          className="mt-2.5 h-11 w-full rounded-sm border-slate-300 text-sm font-semibold text-slate-800 transition-colors duration-200 hover:bg-slate-100 disabled:opacity-50"
          data-testid="email-quote-btn"
        >
          {emailing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
          {emailing ? "Sending…" : "Email Me This Quote"}
        </Button>
        <p className="mt-3 text-center text-[11px] leading-relaxed text-slate-400">
          {m ? "Enter your details and calculate to see your itemized quote." : "Estimate based on regional benchmarks. Final pricing subject to service agreement."}
        </p>
      </div>
    </div>
  );
}
